import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import PersonNameFields from '@/components/PersonNameFields';
import { UserRoles } from '@/contexts/auth/enums';
import { useRegistrationHandler } from '@/components/auth/RegistrationHandler';
import TermsOfServiceModal from '@/components/legal/TermsOfServiceModal';

const ParticipantForm = () => {
  const { register } = useRegistrationHandler();
  const [isLoading, setIsLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [academicTitle, setAcademicTitle] = useState('');
  const [formalSalutation, setFormalSalutation] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName) newErrors.firstName = "Kötelező mező";
    if (!lastName) newErrors.lastName = "Kötelező mező";
    if (!email) newErrors.email = "Kötelező mező";
    if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Érvénytelen email formátum";
    if (!password) newErrors.password = "Kötelező mező";
    if (password.length < 6) newErrors.password = "A jelszónak legalább 6 karakter hosszúnak kell lennie";
    if (password !== confirmPassword) newErrors.confirmPassword = "A jelszavak nem egyeznek";
    if (!termsAccepted) newErrors.terms = "A regisztrációhoz el kell fogadnia a feltételeket";

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(
        { firstName, lastName, academicTitle, formalSalutation },
        email,
        password,
        undefined,
        UserRoles.USER
      );

      if (!result.success && result.message) {
        setFormErrors(prev => ({
          ...prev,
          ...(result.errors || {}),
          general: result.message
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setTermsModalOpen(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {formErrors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {formErrors.general}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        {/* First row - Personal information */}
        <div>
          <PersonNameFields
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            academicTitle={academicTitle}
            setAcademicTitle={setAcademicTitle}
            formalSalutation={formalSalutation}
            setFormalSalutation={setFormalSalutation}
            required={true}
            errors={{
              firstName: formErrors.firstName,
              lastName: formErrors.lastName
            }}
          />
        </div>

        {/* Second row - Account information */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email cím</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              required
              className={formErrors.email ? "border-red-500" : ""}
            />
            {formErrors.email && (
              <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">Jelszó</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                showPasswordToggle={true}
                className={formErrors.password ? "border-red-500" : ""}
              />
              {formErrors.password && (
                <p className="text-sm text-red-500 mt-1">{formErrors.password}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Jelszó megerősítése</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                showPasswordToggle={true}
                className={formErrors.confirmPassword ? "border-red-500" : ""}
              />
              {formErrors.confirmPassword && (
                <p className="text-sm text-red-500 mt-1">{formErrors.confirmPassword}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2 mt-4">
        <Checkbox
          id="terms"
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked === true)}
          className={formErrors.terms ? "border-red-500" : ""}
        />
        <label htmlFor="terms" className={`text-sm text-muted-foreground ${formErrors.terms ? "text-red-500" : ""}`}>
          Elfogadom a <a href="#" onClick={handleTermsClick} className="underline">felhasználási feltételeket</a>
        </label>
      </div>
      {formErrors.terms && (
        <p className="text-sm text-red-500 mt-1">{formErrors.terms}</p>
      )}

      <Button type="submit" className="w-full mt-4" disabled={isLoading}>
        {isLoading ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Regisztráció...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Regisztráció
          </span>
        )}
      </Button>
      
      {/* Terms of Service Modal */}
      <TermsOfServiceModal 
        open={termsModalOpen} 
        onOpenChange={setTermsModalOpen} 
      />
    </form>
  );
};

export default ParticipantForm;
